import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Upload } from "lucide-react";
import { API } from "@/lib/brand";

const NATURES = [
  "Investment-related",
  "Insurance-related",
  "Service/Advisory-related",
  "Billing/Payment-related",
  "Other",
];

export default function Grievance() {
  const [form, setForm] = useState({
    full_name: "", contact_number: "", email: "", client_id: "",
    nature: "", issue_date: "", description: "", resolution_sought: "",
    supporting_document_name: "", declaration: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "declaration" ? e.target.checked : e.target.value }));

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (file) setForm((f) => ({ ...f, supporting_document_name: file.name }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const required = ["full_name", "contact_number", "email", "nature", "description"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error("Please complete all required fields.");
        return;
      }
    }
    if (!form.declaration) {
      toast.error("Please accept the declaration to submit.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, issue_date: form.issue_date || null, client_id: form.client_id || null, resolution_sought: form.resolution_sought || null, supporting_document_name: form.supporting_document_name || null };
      const { data } = await axios.post(`${API}/grievance`, payload);
      setTicket(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (ticket) {
    return (
      <div>
        <section className="border-b hairline">
          <div className="max-w-4xl mx-auto px-5 md:px-8 py-20 md:py-28 text-center" data-testid="grievance-success">
            <div className="inline-flex items-center gap-2 text-[#16A34A]">
              <CheckCircle2 size={20} />
              <span className="eyebrow !text-[#16A34A]">Grievance received</span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl mt-6 text-ink leading-tight">
              Your concern has been logged.
            </h1>
            <div className="mt-10 inline-block card-paper px-8 py-6 ledger-rule">
              <div className="eyebrow mb-2">Your reference number</div>
              <div className="num text-3xl md:text-4xl text-brass-deep tracking-widest" data-testid="grievance-ref">
                {ticket.reference_number}
              </div>
            </div>
            <p className="mt-10 text-[15.5px] text-ink-soft leading-relaxed max-w-2xl mx-auto">
              We will acknowledge your complaint within <span className="text-ink font-medium">3 working days</span> and
              aim to resolve it within <span className="text-ink font-medium">15 working days</span>. If it needs more
              time, we'll let you know why. A copy has been emailed to{" "}
              <span className="num text-ink">{ticket.email}</span>.
            </p>
            <p className="mt-8 text-[13px] text-ink-soft/90 leading-relaxed max-w-2xl mx-auto">
              If your concern remains unresolved after our response, you may escalate it to AMFI (for mutual fund
              matters) at amfiindia.com, or to IRDAI / the Insurance Ombudsman (for insurance matters) at irdai.gov.in.
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-20 pb-14">
          <div className="eyebrow mb-6">Grievance Redressal</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink max-w-4xl">
            We take client concerns<br /><span className="italic text-brass-deep">seriously</span>.
          </h1>
          <p className="mt-8 text-[16px] md:text-[17px] text-ink-soft leading-relaxed max-w-3xl">
            If something hasn't gone the way you expected — a delay, a miscommunication, an error in
            your account, or anything else — please let us know. You can write to us directly, or
            use the form below so your concern is logged and tracked properly.
          </p>
          <p className="mt-6 text-[14px] text-ink-soft">
            Email:{" "}
            <a href="mailto:grievance@reliservewealth.com" className="link-brass num" data-testid="grievance-email-link">
              grievance@reliservewealth.com
            </a>
          </p>
        </div>
      </section>

      <section className="border-b hairline bg-paper-raised">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="card-paper p-6 md:p-10">
            <form onSubmit={onSubmit} className="space-y-6" data-testid="grievance-form">
              <div className="grid md:grid-cols-2 gap-5">
                <GField label="Full Name" required>
                  <input type="text" required value={form.full_name} onChange={upd("full_name")} className="fld" data-testid="griev-input-name" />
                </GField>
                <GField label="Contact Number" required>
                  <input type="tel" required value={form.contact_number} onChange={upd("contact_number")} className="fld" data-testid="griev-input-phone" />
                </GField>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <GField label="Email Address" required>
                  <input type="email" required value={form.email} onChange={upd("email")} className="fld" data-testid="griev-input-email" />
                </GField>
                <GField label="Client ID / Folio / Policy Number">
                  <input type="text" value={form.client_id} onChange={upd("client_id")} className="fld" data-testid="griev-input-clientid" placeholder="Optional — helps us locate the account faster" />
                </GField>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <GField label="Nature of Complaint" required>
                  <select required value={form.nature} onChange={upd("nature")} className="fld" data-testid="griev-input-nature">
                    <option value="" disabled>Select…</option>
                    {NATURES.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </GField>
                <GField label="Date the Issue Occurred">
                  <input type="date" value={form.issue_date} onChange={upd("issue_date")} className="fld" data-testid="griev-input-date" />
                </GField>
              </div>

              <GField label="Description of the Complaint" required>
                <textarea rows={5} required value={form.description} onChange={upd("description")} className="fld resize-none" placeholder="Please describe what happened, in as much detail as possible" data-testid="griev-input-description" />
              </GField>

              <GField label="What Resolution Are You Looking For?">
                <textarea rows={3} value={form.resolution_sought} onChange={upd("resolution_sought")} className="fld resize-none" data-testid="griev-input-resolution" />
              </GField>

              <div>
                <span className="eyebrow block mb-2">Supporting Document</span>
                <label className="fld flex items-center gap-3 cursor-pointer">
                  <Upload size={16} className="text-brass shrink-0" />
                  <span className="text-[14px] text-ink-soft">
                    {form.supporting_document_name || "Attach a screenshot, statement, or email trail (optional)"}
                  </span>
                  <input type="file" className="hidden" onChange={onFile} data-testid="griev-input-file" />
                </label>
                <p className="text-[11px] text-ink-soft/80 mt-1">Only the filename is stored for reference. Please email large attachments to grievance@reliservewealth.com.</p>
              </div>

              <label className="flex items-start gap-3 text-[14px] text-ink-soft leading-relaxed cursor-pointer">
                <input type="checkbox" checked={form.declaration} onChange={upd("declaration")} className="mt-1 accent-[#14A5A5]" data-testid="griev-input-declaration" />
                <span>
                  <span className="text-[#DC2626]">*</span> I confirm the information provided above is accurate to the
                  best of my knowledge.
                </span>
              </label>

              <button type="submit" disabled={submitting} className="btn-brass w-full justify-center" data-testid="griev-submit">
                {submitting ? "Submitting…" : (<>Submit Grievance <ArrowRight size={16} /></>)}
              </button>
            </form>
          </div>

          <div className="mt-8 p-6 border-l-2 border-[#DC2626] bg-paper">
            <div className="eyebrow !text-[#DC2626]">Escalation</div>
            <p className="text-[14px] text-ink-soft leading-relaxed mt-2">
              If your concern remains unresolved after our response, you may escalate it to AMFI (for mutual
              fund–related matters) at <a href="https://amfiindia.com" className="link-brass" target="_blank" rel="noreferrer">amfiindia.com</a>,
              or to IRDAI / the Insurance Ombudsman (for insurance-related matters) at{" "}
              <a href="https://irdai.gov.in" className="link-brass" target="_blank" rel="noreferrer">irdai.gov.in</a>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function GField({ label, required, children }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}{required && <span className="text-[#DC2626]"> *</span>}</span>
      {children}
    </label>
  );
}
